import { Suspense, type ChangeEvent, type Ref } from "react";
import { useTranslation } from "react-i18next";

import type { Profile } from "@/api/bale.zod";

import {
  useCreateFriendRequest,
  useDismissFriendRequest,
  useGetProfileSuspense,
  useListFriendRequestsSuspense,
  useListFriendsSuspense,
  useSearchProfiles,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { CardActionsHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { ProfileItem } from "@/components/ProfileItem";
import { useDebouncedState } from "@/hooks/useDebouncedState";

export function AddFriendModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useDebouncedState("", 1000 / 3);

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.currentTarget.value);
  };

  const friendSearch = useSearchProfiles(
    { query: searchValue.trim() },
    {
      query: {
        enabled: !!searchValue.trim(),
        select: (data) => data.data.profiles,
      },
    },
  );

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="flex h-[95vh] max-h-200 w-lg flex-col">
      <CardActionsHeader title={t("add-friend")}>
        <Input type="search" name="id" onChange={onSearch} placeholder={t("search-by-name")} />
      </CardActionsHeader>
      <div className="flex min-h-0 grow flex-col overflow-y-auto">
        {searchValue === "" && (
          <div className="flex grow items-center justify-center p-8 font-interest text-placeholder">
            {t("search-for-a-friend")}
          </div>
        )}
        {friendSearch.isFetching && (
          <div className="flex grow items-center justify-center p-8 font-interest text-placeholder">
            <Icon name="loader" className="animate-spin" />
          </div>
        )}
        {friendSearch.isSuccess && friendSearch.data?.length === 0 && (
          <div className="flex grow items-center justify-center p-8 font-interest text-placeholder">
            {t("no-profiles-found")}
          </div>
        )}
        {friendSearch.data?.map((profile) => (
          <ProfileListItem key={profile.id} profile={profile} />
        ))}
      </div>
    </Dialog>
  );
}

function ProfileListItem({ profile }: { profile: Profile }) {
  return (
    <div className="flex flex-row items-center justify-between gap-4 p-2 pl-4">
      <ProfileItem profile={profile} />
      <Suspense fallback={<IconButton type="button" name="user" disabled />}>
        <FriendRequestButton profile={profile} />
      </Suspense>
    </div>
  );
}

function FriendRequestButton({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const createFriendRequest = useCreateFriendRequest();
  const dismissFriendRequest = useDismissFriendRequest();
  const me = useGetProfileSuspense("@me", {
    query: { select: (data) => data.data.profile },
  });
  const listFriends = useListFriendsSuspense("@me", {
    query: { select: (data) => data.data.friends },
  });
  const listFriendRequests = useListFriendRequestsSuspense({
    query: { select: (data) => data.data },
  });

  const isFriend = listFriends.data.some((friend) => friend.profile.id === profile.id);
  if (isFriend) {
    return (
      <div className="flex flex-row items-center gap-2">
        <span className="font-interest text-placeholder">{t("friends")}</span>
        <IconButton type="button" name="user-check" disabled />
      </div>
    );
  }

  const isReceived = listFriendRequests.data.received_friend_requests.some(
    (friend) => friend.profile.id === profile.id,
  );
  if (isReceived) {
    return (
      <ActionBox>
        <span className="font-interest">{t("requested")}</span>
        <IconButton
          type="button"
          name="x"
          onClick={() => dismissFriendRequest.mutate({ data: { friend_id: profile.id } })}
        />
        <IconButton
          type="button"
          name="check"
          onClick={() => createFriendRequest.mutate({ data: { friend_id: profile.id, note: null } })}
        />
      </ActionBox>
    );
  }

  const isSent = listFriendRequests.data.sent_friend_requests.some((friend) => friend.profile.id === profile.id);
  if (isSent) {
    return (
      <ActionBox>
        <span className="font-interest text-placeholder">{t("requested")}</span>
        <IconButton
          type="button"
          name="x"
          onClick={() => dismissFriendRequest.mutate({ data: { friend_id: profile.id } })}
        />
      </ActionBox>
    );
  }

  const isMe = profile.id === me.data.id;
  if (isMe) {
    return (
      <div className="flex flex-row items-center gap-2 pr-2">
        <span className="font-interest text-placeholder">{t("this-is-you")}</span>
      </div>
    );
  }

  return (
    <ActionBox>
      <span className="font-interest text-placeholder">{t("not-friends")}</span>
      <IconButton
        type="button"
        name="user-plus"
        onClick={() => createFriendRequest.mutate({ data: { friend_id: profile.id, note: null } })}
      />
    </ActionBox>
  );
}
